import { Button, Stack, Typography } from '@mui/material';
import { useMsalIdToken } from '../../../../../hooks/useMsalIdToken';
import { useEffect, useState } from 'react';
import { FormInput } from '../../../../../components/form';
import { useUpdateAddressMutation } from '../../../../../services/parent.service';
import { SuccessView } from '../success';
import { ErrorView } from '../error';
import { Dialog } from '../../../../../components/dialog';
import { AddressType } from '../../../../../types/address.type';

const feedback = `Please be aware, that for privacy reasons, this address request does not automatically change the address record for another parent for whom the School holds the same address.`;
const manualAddressMessage = `Please complete this form if you are unable to find your address in our look up, or if your residential address is not in Australia. This will be sent to the studies office for them to update manually. Your profile will not update immediately with this change.`;

interface ManualAddressProps {
  isOpen: boolean;
  handleManualAddressClose: () => void;
}

const ManualAddress = ({
  isOpen,
  handleManualAddressClose,
}: ManualAddressProps) => {
  const token = useMsalIdToken();

  const [residentialAddress, setResidentialAddress] = useState({
    street: '',
    suburb: '',
    zipCode: '',
    country: '',
    state: '',
  });

  const [selectedResidentail, setSelectedResidential] =
    useState<AddressType | null>(null);

  const [
    updateAddress,
    {
      isLoading: isUpdating,
      isSuccess: isUpdateSuccess,
      isError: isUpdateError,
    },
  ] = useUpdateAddressMutation();

  const handleSubmit = () => {
    const ra = residentialAddress;
    const sr = selectedResidentail;
    const hasChange =
      ra.state !== sr?.state ||
      ra.suburb !== sr?.suburb ||
      ra.zipCode !== sr?.postcode ||
      ra.street !== sr?.line1 ||
      ra.country !== sr?.country;

    const payload = {
      residentialAddress,
      recommendedAddress: '',
      hasChange,
      manualAddressInput: true,
    };

    void updateAddress({ token, payload });
  };

  useEffect(() => {
    setResidentialAddress({
      street: selectedResidentail?.line1 ?? '',
      suburb: selectedResidentail?.suburb ?? '',
      zipCode: selectedResidentail?.postcode ?? '',
      country: selectedResidentail?.country ?? '',
      state: selectedResidentail?.state ?? '',
    });
  }, [selectedResidentail]);

  return (
    <Dialog
      open={isOpen}
      handleClose={handleManualAddressClose}
      title={!isUpdateSuccess ? 'Submit Address Manually' : ''}
      actions={
        isUpdateSuccess ? null : (
          <Stack
            flex={1}
            flexDirection='row'
            alignItems='center'
            justifyContent='space-between'
            py={1}
            px={3}
          >
            <Button onClick={handleManualAddressClose} variant='outlined'>
              Cancel
            </Button>
            <Button
              variant='contained'
              color='primary'
              type='submit'
              onClick={() => handleSubmit()}
              disabled={isUpdating}
            >
              {isUpdating ? 'Sending...' : 'Send'}
            </Button>
          </Stack>
        )
      }
    >
      {isUpdateSuccess ? (
        <SuccessView />
      ) : (
        <Stack gap={2} py={3} minWidth={{ xs: 300, md: 500 }} maxWidth={600}>
          <Stack overflow='auto' height='100%'>
            <Stack pb={2} gap={2}>
              <Typography fontSize={14} fontWeight={400} pb={0}>
                {manualAddressMessage}
              </Typography>
              <>
                <FormInput
                  label='Street'
                  id='residential-street'
                  value={residentialAddress?.street}
                  onChange={(event) => {
                    setResidentialAddress((prevState) => ({
                      ...prevState,
                      street: event.target.value,
                    }));
                  }}
                />

                <FormInput
                  label='Postcode'
                  value={residentialAddress?.zipCode}
                  onChange={(event) => {
                    setResidentialAddress((prevState) => ({
                      ...prevState,
                      zipCode: event.target.value,
                    }));
                  }}
                />

                <FormInput
                  label='Suburb'
                  value={residentialAddress?.suburb}
                  onChange={(event) => {
                    setResidentialAddress((prevState) => ({
                      ...prevState,
                      suburb: event.target.value,
                    }));
                  }}
                />

                <FormInput
                  label='State'
                  value={residentialAddress?.state}
                  onChange={(event) => {
                    setResidentialAddress((prevState) => ({
                      ...prevState,
                      state: event.target.value,
                    }));
                  }}
                />

                <FormInput
                  label='Country'
                  value={residentialAddress?.country}
                  onChange={(event) => {
                    setResidentialAddress((prevState) => ({
                      ...prevState,
                      country: event.target.value,
                    }));
                  }}
                />
              </>

              <Stack>
                <Typography variant='caption'>{feedback}</Typography>
              </Stack>
            </Stack>

            <ErrorView isOpen={isUpdateError} />
          </Stack>
        </Stack>
      )}
    </Dialog>
  );
};

export { ManualAddress };
